"use client";

import React, { useMemo, useState, useRef, useEffect } from "react";
import { useSearchParams, usePathname } from "next/navigation";
import { useDebounce } from "use-debounce";
import { PortfolioProject } from "../lib/portfolio";
import PortfolioFilters from "./PortfolioFilters";
import PortfolioGrid from "./PortfolioGrid";

interface PortfolioContentProps {
  allProjects: PortfolioProject[];
  categories: string[];
}

export default function PortfolioContent({ allProjects = [], categories = [] }: PortfolioContentProps) {
  const searchParams = useSearchParams();
  const pathname = usePathname();

  // URL source of truth for initial load
  const initialQuery = searchParams ? searchParams.get("q") || "" : "";
  const initialCategory = searchParams ? searchParams.get("category") || "All Projects" : "All Projects";
  const initialPage = searchParams ? Number(searchParams.get("page")) || 1 : 1;

  // Local state for UI responsiveness and to avoid server re-fetches
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [activeCategory, setActiveCategory] = useState(initialCategory);
  const [page, setPage] = useState(initialPage);
  
  const [debouncedSearchQuery] = useDebounce(searchQuery, 300);
  
  const searchUrlTimer = useRef<number | null>(null);

  // Sync with browser back/forward buttons
  useEffect(() => {
    const q = searchParams?.get("q") || "";
    const cat = searchParams?.get("category") || "All Projects";
    const p = Number(searchParams?.get("page")) || 1;
    
    setSearchQuery(q);
    setActiveCategory(cat);
    setPage(p);
  }, [searchParams]);

  // Filtering happens instantly in memory using the fetched `allProjects`
  const filteredProjects = useMemo(() => {
    if (!Array.isArray(allProjects)) return [];

    return allProjects.filter((p) => {
      if (!p) return false;
      
      const matchesCategory =
        activeCategory === "All Projects" || (p.categories && p.categories.includes(activeCategory));
      
      const pTitle = (p.title || "").toLowerCase();
      const pDesc = (p.description || "").toLowerCase();
      const sQuery = (debouncedSearchQuery || "").toLowerCase();

      const matchesQuery =
        !sQuery ||
        pTitle.includes(sQuery) ||
        pDesc.includes(sQuery);

      return matchesCategory && matchesQuery;
    });
  }, [allProjects, debouncedSearchQuery, activeCategory]);

  // Update URL without triggering a Next.js server re-fetch
  const updateUrlSilently = (q: string, cat: string, p: number) => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (cat !== "All Projects") params.set("category", cat);
    if (p > 1) params.set("page", p.toString());
    
    const newUrl = params.toString() ? `${pathname}?${params.toString()}` : pathname;
    
    // Using window.history.replaceState prevents Next.js from re-running the Server Component
    window.history.replaceState({ ...window.history.state, as: newUrl, url: newUrl }, "", newUrl);
  };

  const handleSearch = (term: string) => {
    setSearchQuery(term);
    setPage(1);

    if (searchUrlTimer.current) {
      window.clearTimeout(searchUrlTimer.current);
    }

    searchUrlTimer.current = window.setTimeout(() => {
      updateUrlSilently(term, activeCategory, 1);
    }, 300);
  };

  const handleCategory = (category: string) => {
    setActiveCategory(category);
    setPage(1);
    updateUrlSilently(searchQuery, category, 1);
  };

  // Pagination handler
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    updateUrlSilently(searchQuery, activeCategory, newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <section className="max-w-[1440px] mx-auto px-margin-mobile md:px-margin-desktop mb-16 flex flex-col items-center text-center">
        <div className="w-full mb-12 flex justify-center">
          <div className="relative w-full max-w-2xl group">
            <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 transition-colors text-[20px] text-secondary group-focus-within:text-primary">
              search
            </span>
            <input
              aria-label="Search projects"
              className="w-full bg-transparent border border-surface-variant focus:border-primary focus:ring-1 focus:ring-primary pl-12 pr-4 py-3 font-label-sm text-label-sm uppercase tracking-widest text-primary placeholder:text-secondary/50 transition-all rounded-none"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="SEARCH PROJECTS..."
              type="search"
            />
          </div>
        </div>
        <PortfolioFilters 
          categoriesProp={categories} 
          activeCategoryExternal={activeCategory}
          onCategoryChangeExternal={handleCategory}
        />
      </section>

      <section className="max-w-[1440px] mx-auto px-margin-mobile md:px-margin-desktop flex flex-col items-center">
        <div className="w-full min-h-[600px]">
          <PortfolioGrid 
            projects={filteredProjects} 
            page={page} 
            onPageChange={handlePageChange} 
          />
        </div>
      </section>
    </>
  );
}
